import React, {useEffect, useRef, useState} from 'react';
import {Button} from 'antd';
import Header from '../../components/molecules/Header/component';
import Footer from '../../components/molecules/Footer/component';
import Seo from '../../components/atoms/Seo/component';
import {openSignup} from '../../utils/helpers';
import productService from '../../services/productService';
import {trackEvent} from '../../analytics/posthog';
import {useTrackOnView} from '../../analytics/useTrackOnView';
import './style.css';

const openAdvertiserSignup = () => {
    trackEvent('advertiser_guide_start_as_advertiser_clicked', {
        selected_role: 'advertiser',
        signup_source: 'advertiser_guide_start_as_advertiser'
    });
    openSignup('advertiser', 'advertiser_guide_start_as_advertiser');
};

const benefits = [
    {
        image: '/assets/images/advertiser-guide/performance-first.png',
        title: 'Performance-first',
        text: 'Set your CPC and only pay for real actions, not vague impressions or untracked results.'
    },
    {
        image: '/assets/images/advertiser-guide/brand-control.png',
        title: 'Brand control',
        text: 'Set campaign instructions so promoters represent your business correctly.'
    },
    {
        image: '/assets/images/advertiser-guide/scale-confidence.png',
        title: 'Scale with confidence',
        text: 'Launch, test and increase budget only when the data makes sense.'
    }
];

const steps = [
    {
        title: 'Subscribe as Advertiser',
        text: 'Create your account and unlock advertiser campaign tools.'
    },
    {
        title: 'Go to Create campaign',
        text: 'Add campaign title, description, instructions and your target URL.'
    },
    {
        title: 'Set CPC and budget',
        text: 'Choose your click rate, total click budget and optional geographic rules.'
    },
    {
        title: 'Track verified traffic',
        text: 'Monitor your campaigns and dashboard for clicks, spend and status.'
    }
];

const trackItems = [
    'Clicks generated',
    'Campaign spend and remaining balance',
    'Campaign status',
    'Performance per campaign'
];

const controlGroups = [
    {
        title: 'You control',
        items: ['CPC', 'Budget', 'Campaign instructions', 'Target link']
    },
    {
        title: 'Promotify handles',
        items: ['Tracking', 'Reporting', 'Campaign visibility to promoters']
    }
];

const getPlanSortValue = (product: any) => {
    const price = product.default_price;
    const interval = price?.recurring?.interval;
    const intervalCount = price?.recurring?.interval_count || 1;

    if (interval === 'month') return intervalCount;
    if (interval === 'year') return 12 * intervalCount;

    return Number.MAX_SAFE_INTEGER;
};

const formatPlanFromProduct = (product: any) => {
    const price = product.default_price;
    const amount = typeof price?.unit_amount === 'number' ? price.unit_amount / 100 : 0;
    const currency = price?.currency?.toUpperCase?.() || 'USD';
    const interval = price?.recurring?.interval;
    const intervalCount = price?.recurring?.interval_count || 1;
    const cadence = interval
        ? intervalCount === 1
            ? `Per ${interval}`
            : `Every ${intervalCount} ${interval}s`
        : '';
    return {
        name: product.name.replace(' - Advertiser', ''),
        price: `$${amount.toFixed(2)}`,
        cadence,
        currency,
        image: product.images?.[0],
        description: product.description
    };
};

const faqs = [
    {
        question: 'What do I actually pay for?',
        answer: 'Tracked clicks to your target link, such as your website, booking page, socials or another destination.'
    },
    {
        question: 'Can I change my budget later?',
        answer: 'Yes. You can top up campaigns and adjust spending as needed.'
    },
    {
        question: 'Do I need to find promoters myself?',
        answer: 'No. Your campaign is visible to promoters who browse and choose campaigns to promote.'
    },
    {
        question: 'Does Promotify guarantee sales?',
        answer: 'No. Promotify drives tracked traffic; outcomes depend on your offer, landing page and follow-up.'
    }
];

const scrollToPlan = () => {
    trackEvent('advertiser_guide_see_pricing_clicked', {
        selected_role: 'advertiser'
    });
    document.getElementById('advertiser-plan')?.scrollIntoView({behavior: 'smooth'});
};

const AdvertiserGuidePage: React.FC = () => {
    const [advertiserProducts, setAdvertiserProducts] = useState<any[]>([]);
    const [isLoadingProducts, setIsLoadingProducts] = useState(true);
    const pricingSectionRef = useRef<HTMLElement>(null);
    const planOptionsRef = useRef<HTMLDivElement>(null);

    useTrackOnView(pricingSectionRef, 'advertiser_guide_pricing_section_viewed', {
        selected_role: 'advertiser',
        section_name: 'advertiser_pricing'
    });
    useTrackOnView(planOptionsRef, 'advertiser_guide_plan_viewed', {
        selected_role: 'advertiser',
        plan_name: 'Advertiser'
    });

    useEffect(() => {
        window.scrollTo({top: 0, left: 0, behavior: 'auto'});
        trackEvent('advertiser_guide_page_viewed', {
            selected_role: 'advertiser'
        });
    }, []);

    useEffect(() => {
        productService.getProducts('advertiser', {skipSubscriptionCheck: true})
            .then(products => {
                setAdvertiserProducts(Array.isArray(products) ? products : []);
            })
            .finally(() => setIsLoadingProducts(false));
    }, []);

    const advertiserPlans = [...advertiserProducts]
        .sort((a, b) => getPlanSortValue(a) - getPlanSortValue(b))
        .map(formatPlanFromProduct);

    return (
    <div className="advertiserGuide">
        <Seo
            title="Advertiser Guide | Promotify"
            description="Learn how advertisers use Promotify to launch tracked click campaigns, set CPC budgets, and monitor transparent campaign performance."
            canonicalPath="/advertiser-guide"
        />
        <Header/>

        <main>
            <section className="advertiserGuideHero">
                <div className="advertiserGuideDecor decorRing"/>
                <div className="advertiserGuideDecor decorPlus">+</div>
                <div className="advertiserGuideDecor decorDot"/>
                <div className="advertiserGuideContainer advertiserGuideHeroGrid">
                    <div className="advertiserGuideHeroCopy">
                        <span className="advertiserGuideEyebrow">Advertiser guide</span>
                        <h1>
                            Pay for tracked clicks,
                            <span> not vague influencer promises.</span>
                        </h1>
                        <p>
                            Set your cost per click, set your budget, and watch traffic arrive in real time.
                        </p>
                        <div className="advertiserGuideActions">
                            <Button type="primary" onClick={openAdvertiserSignup}>Start as Advertiser</Button>
                            <Button onClick={scrollToPlan}>See pricing</Button>
                        </div>
                        <small>Launch a campaign, publish instructions, and pay only for tracked click traffic.</small>
                    </div>

                    <div className="advertiserGuideHeroImage">
                        <div className="advertiserGuideSnapshot">
                            <strong>Quick snapshot</strong>
                            <ul>
                                <li>Pay only for clicks (CPC).</li>
                                <li>Unlimited campaigns on the $95/mo Advertiser plan.</li>
                                <li>Set your budget and scale anytime.</li>
                                <li>Real-time dashboard: clicks, spend, promoters.</li>
                            </ul>
                        </div>
                        <img
                            src="/assets/images/advertiser-guide/advertiser-main.png"
                            alt="Advertiser campaign growth illustration"
                        />
                    </div>
                </div>
            </section>

            <section className="advertiserGuideSection advertiserGuideIntro">
                <div className="advertiserGuideContainer advertiserGuideNarrow">
                    <span className="advertiserGuideEyebrow">Why PromotifyAI</span>
                    <h2>Most influencer deals are paid upfront with unclear results.</h2>
                    <p>
                        Promotify flips the model: you set CPC and budget, promoters drive traffic to your link, and
                        your dashboard shows performance clearly.
                    </p>
                </div>

                <div className="advertiserGuideContainer advertiserGuideCards">
                    {benefits.map((card, index) => (
                        <article className={`advertiserGuideCard card${index + 1}`} key={card.title}>
                            <img src={card.image} alt="" aria-hidden="true"/>
                            <h3>{card.title}</h3>
                            <p>{card.text}</p>
                        </article>
                    ))}
                </div>
            </section>

            <section className="advertiserGuideSection advertiserGuideStepsSection">
                <div className="advertiserGuideContainer advertiserGuideNarrow">
                    <span className="advertiserGuideEyebrow">How it works</span>
                    <h2>Launch with a simple campaign flow.</h2>
                </div>
                <div className="advertiserGuideContainer advertiserGuideSteps">
                    {steps.map((step, index) => (
                        <article className="advertiserGuideStep" key={step.title}>
                            <div className="advertiserGuideStepTop">
                                <span className="advertiserGuideNumber">{index + 1}</span>
                                {index < steps.length - 1 && (
                                    <span className="advertiserGuideStepArrow" aria-hidden="true"/>
                                )}
                            </div>
                            <div>
                                <h3>{step.title}</h3>
                                <p>{step.text}</p>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            <section className="advertiserGuideSection advertiserGuideDashboardSection">
                <div className="advertiserGuideContainer advertiserGuideDashboard">
                    <div>
                        <span className="advertiserGuideEyebrow">What you can track</span>
                        <h2>Your campaign dashboard. Full control in one place.</h2>
                        <ul className="advertiserGuideCheckList">
                            {trackItems.map(item => <li key={item}>{item}</li>)}
                        </ul>
                    </div>
                    <div className="advertiserGuideMockup" aria-hidden="true">
                        <div className="advertiserGuideMockupStats">
                            <div>
                                <span>Total Spend</span>
                                <strong>$1,326.80</strong>
                            </div>
                            <div>
                                <span>Total Clicks</span>
                                <strong>4,672</strong>
                            </div>
                            <div>
                                <span>Active Campaigns</span>
                                <strong>8</strong>
                            </div>
                        </div>
                        <div className="advertiserGuideChart">
                            <span style={{height: '34%'}}/>
                            <span style={{height: '48%'}}/>
                            <span style={{height: '44%'}}/>
                            <span style={{height: '66%'}}/>
                            <span style={{height: '58%'}}/>
                            <span style={{height: '82%'}}/>
                        </div>
                        <div className="advertiserGuideRows">
                            <div><span>Spring Sale</span><span>Active</span></div>
                            <div><span>Product Launch</span><span>Active</span></div>
                            <div><span>Brand Awareness</span><span>Active</span></div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="advertiserGuideSection advertiserGuideControlSection">
                <div className="advertiserGuideContainer advertiserGuideControlGrid">
                    {controlGroups.map(group => (
                        <article className="advertiserGuideControlCard" key={group.title}>
                            <h2>{group.title}</h2>
                            <ul>
                                {group.items.map(item => <li key={item}>{item}</li>)}
                            </ul>
                        </article>
                    ))}
                </div>
            </section>

            <section ref={pricingSectionRef} className="advertiserGuideSection advertiserGuidePlanSection" id="advertiser-plan">
                <div className="advertiserGuideContainer advertiserGuideNarrow">
                    <span className="advertiserGuideEyebrow">Pricing package</span>
                    <h2>Advertiser Plan</h2>
                </div>

                <div className="advertiserGuidePlan">
                    <div className="advertiserGuidePlanTop">
                        <div>
                            <h3>Grow your business like never before</h3>
                            <ul className="advertiserGuidePlanFeatures">
                                <li>Unlimited campaign creation</li>
                                <li>Set custom click budgets and rates</li>
                                <li>Real-time analytics dashboard</li>
                                <li>Transparent verified click tracking</li>
                                <li>No hidden fees</li>
                            </ul>
                            <div className="advertiserGuidePlanPractices">
                                <h3>Best practices (so you win fast)</h3>
                                <ul>
                                    <li>Be specific with campaign instructions.</li>
                                    <li>Use a focused landing page.</li>
                                    <li>Start with a test budget.</li>
                                    <li>Increase spend once the campaign data looks right.</li>
                                </ul>
                            </div>
                        </div>
                        <div>
                            <div ref={planOptionsRef} className="advertiserGuidePlanOptions">
                                {isLoadingProducts && (
                                    <div className="advertiserGuidePlanStatus">Loading advertiser plans...</div>
                                )}
                                {!isLoadingProducts && advertiserPlans.length === 0 && (
                                    <div className="advertiserGuidePlanStatus">Advertiser plans are unavailable right now.</div>
                                )}
                                {!isLoadingProducts && advertiserPlans.map(plan => (
                                    <article className="advertiserGuidePlanOption" key={plan.name}>
                                        <img
                                            className="advertiserGuidePlanImage"
                                            src={plan.image || '/assets/images/promotify-transparent.png'}
                                            alt={plan.name}
                                        />
                                        <div>
                                            <h3>{plan.name}</h3>
                                            {plan.description && (
                                                <p className="advertiserGuidePlanDescription">
                                                    <em>{plan.description}</em>
                                                </p>
                                            )}
                                        </div>
                                        <p className="advertiserGuidePlanPrice">
                                            {plan.price}<span> {plan.currency}</span>
                                        </p>
                                    </article>
                                ))}
                            </div>
                        </div>
                        <div className="advertiserGuidePlanCta">
                                <Button type="primary" onClick={openAdvertiserSignup}>
                                Sign up
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            <section className="advertiserGuideFaqSection">
                <div className="advertiserGuideContainer">
                    <div className="advertiserGuideFaqCard">
                    <div>
                        <h2>Frequently asked questions</h2>
                        {faqs.map(faq => (
                            <details
                                key={faq.question}
                                onToggle={(event) => {
                                    if ((event.currentTarget as HTMLDetailsElement).open) {
                                        trackEvent('advertiser_guide_faq_opened', {
                                            selected_role: 'advertiser',
                                            faq_question: faq.question
                                        });
                                    }
                                }}
                            >
                                <summary>{faq.question}</summary>
                                <p>{faq.answer}</p>
                            </details>
                        ))}
                    </div>
                    </div>
                </div>
            </section>

            <section className="advertiserGuideAnimationSection">
                <video
                    src="/assets/videos/logo-animation-dark.mp4"
                    autoPlay
                    muted
                    loop
                    playsInline
                    aria-label="Promotify animated logo"
                />
            </section>
        </main>

        <Footer/>
    </div>
    );
};

export default AdvertiserGuidePage;
